import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  LoadingState,
  PageHeader,
  SectionHeader,
} from '../../../group_leader/components/common'
import { makeT, sharedT } from '../../../group_leader/helpers/i18n'
import { formatHandicap, playerInitials } from '../../../group_leader/helpers/player'
import { isPresent } from '../../../group_leader/helpers/value'
import {
  glApi,
  useListRoundsQuery,
  useGetTeeSheetQuery,
  useGetUnplacedPlayersQuery,
  useMemberSearchQuery,
  useListGroupEventsQuery,
  useListGroupRosterQuery,
  useImportMembersMutation,
  useEnrollInSkinsMutation,
  useEnrollRosterInSkinsMutation,
  useRemoveMemberMutation,
  useUpdateMemberMutation,
  useCreateMemberMutation,
} from '../../../group_leader/store/glApi'
import { showErrorNotification, isValidEmail } from '../../../shared/helpers'
import LiveMessage from '../common/LiveMessage'
import WizardFooter from '../common/WizardFooter'
import GolferResultRow from '../common/GolferResultRow'
import GolferSearchInput from '../common/GolferSearchInput'
import MemberFormFields from './players/MemberFormFields'
import SourceTiles from './players/SourceTiles'
import SourcePicker from './players/SourcePicker'
import useEnrollGolfer from './players/useEnrollGolfer'
import useJobPoll from '../../hooks/useJobPoll'
import { REORGANIZE_OUTCOME } from '../../helpers/pairings'
import {
  EMPTY_MEMBER_FORM,
  enrolledPlayers,
  getCustomField,
  parseHandicap,
  previousEventRows,
  SEARCH_DEBOUNCE_MS,
} from '../../helpers/players'
import { goToNextStep, goToPreviousStep, markStepCompleted } from '../../store/wizardSlice'

const MIN_SEARCH_LENGTH = 2

const t = makeT('group_leader.event_setup.players')

// Set updaters for the optimistic id lists, so a failed enrol can take its id back out.
const withIds = (...ids) => (prev) => new Set([ ...prev, ...ids ])
const withoutId = (id) => (prev) => {
  const next = new Set(prev)
  next.delete(id)
  return next
}

const PlayersStep = () => {
  const dispatch = useDispatch()
  const leagueId = useSelector((s) => s.misc.data.leagueId)
  const roundId = useSelector((s) => s.wizard.roundId)
  const groupRosterLeagueId = useSelector((s) => s.misc.data.groupRosterLeagueId)
  const groupId = useSelector((s) => s.misc.data.groupId)
  const phoneFieldName = useSelector((s) => s.misc.data.phoneFieldName)

  const [ searchTerm, setSearchTerm ] = useState('')
  // The text the last Enter added from, so a double Enter on it is ignored.
  const enteredTermRef = useRef(null)
  const [ liveMessage, setLiveMessage ] = useState('')
  // Toasted and announced: react-notifications carries no aria-live of its own.
  const notifyFailure = (message) => { showErrorNotification(message); setLiveMessage(message) }
  const [ debouncedQ, setDebouncedQ ] = useState('')
  const [ enrolledMemberIds, setEnrolledMemberIds ] = useState(new Set())
  const [ confirmId, setConfirmId ] = useState(null)
  const [ editingPlayerId, setEditingPlayerId ] = useState(null)
  const [ editingPlayerObj, setEditingPlayerObj ] = useState(null)
  // Saved edits by member id; the refetch still carries the old name snapshot.
  const [ memberEdits, setMemberEdits ] = useState({})
  const [ editForm, setEditForm ] = useState(EMPTY_MEMBER_FORM)
  const [ showAddForm, setShowAddForm ] = useState(false)
  const [ addForm, setAddForm ] = useState(EMPTY_MEMBER_FORM)
  const [ addSubmitAttempted, setAddSubmitAttempted ] = useState(false)
  const [ editSubmitAttempted, setEditSubmitAttempted ] = useState(false)

  const debounceRef = useRef(null)
  const handleSearch = (val) => {
    setSearchTerm(val)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedQ(val), SEARCH_DEBOUNCE_MS)
  }

  // Only for the walk-on check; served from the cache every other step fills.
  const { data: roundsData } = useListRoundsQuery(leagueId, { skip: !leagueId })

  const { data: teeSheetData, isLoading: sheetLoading } = useGetTeeSheetQuery(
    { leagueId, roundId },
    { skip: !leagueId || !roundId },
  )

  const { data: unplacedData } = useGetUnplacedPlayersQuery(
    { leagueId, roundId },
    { skip: !leagueId || !roundId },
  )

  const { data: searchData, isFetching: searching } = useMemberSearchQuery(
    { leagueId, roundId, q: debouncedQ },
    { skip: debouncedQ.length < MIN_SEARCH_LENGTH || !roundId },
  )

  // One golfer joins the round the same way whichever list offered them (see useEnrollGolfer).
  const { enrollGolfer, enrolling } = useEnrollGolfer({ leagueId, roundId })
  const [ importMembers, { isLoading: importStarting } ] = useImportMembersMutation()
  const pollJob = useJobPoll({ leagueId, roundId })
  // The job runs past the 202, so "adding" lasts until the poll ends.
  const [ addAllRunning, setAddAllRunning ] = useState(false)
  const addingAll = importStarting || addAllRunning
  // The source the tiles have open; search by default.
  const [ activeSource, setActiveSource ] = useState('search')
  // Golfers enrolled from the panel, hidden until the tee sheet refetch removes them.
  const [ rosterAddedIds, setRosterAddedIds ] = useState(new Set())
  // Manual golfers skip useEnrollGolfer: create_member makes the member in this league outright.
  const [ enrollInSkins ] = useEnrollInSkinsMutation()
  const [ enrollRosterInSkins ] = useEnrollRosterInSkinsMutation()
  const [ removeMember, { isLoading: unenrolling } ] = useRemoveMemberMutation()
  const [ updateMember, { isLoading: saving } ] = useUpdateMemberMutation()
  const [ createMember, { isLoading: creating } ] = useCreateMemberMutation()

  // Here so the tiles know which sources are empty; the picker reads the same cache.
  const { data: groupEventsData, isFetching: groupEventsLoading } = useListGroupEventsQuery(
    { groupId }, { skip: !groupId },
  )
  const { data: groupRosterData, isFetching: groupRosterLoading } = useListGroupRosterQuery({ page: 1 })
  const previousEventCount = previousEventRows(groupEventsData?.data, leagueId).length
  const emptySources = {
    // Unknown while loading is not empty: a tile must not flicker to disabled and back.
    previous_event: !groupEventsLoading && previousEventCount === 0,
    roster: !groupRosterLoading && (groupRosterData?.members || []).length === 0,
  }

  const foursomes = teeSheetData?.data || []
  const unplaced = unplacedData?.players || []
  // Group size 1 on the round, not the wizard config: this step runs before Pairings.
  const round = roundsData?.data?.find((entry) => String(entry.id) === String(roundId))
    || roundsData?.data?.[0]
  const isWalkon = String(round?.attributes?.pairing_group_size) === '1'
  const { placed, allEnrolled } = enrolledPlayers(foursomes, unplaced)

  // From the roster, not Continue: Redux does not survive a reload.
  useEffect(() => {
    if (allEnrolled.length > 0) { dispatch(markStepCompleted('players')) }
  }, [ allEnrolled.length ])

  // Overlay any saved-but-not-yet-reflected member edits onto the enrolled list.
  const displayedEnrolled = allEnrolled.map((player) => {
    const edit = player.player_roster_id ? memberEdits[player.player_roster_id] : null
    return edit ? { ...player, ...edit } : player
  })

  const members = (searchData?.members || []).filter((m) => !enrolledMemberIds.has(m.member_id))

  const emailError = (form, attempted) => Boolean(attempted && form.email.trim() && !isValidEmail(form.email.trim()))
  const addEmailError = emailError(addForm, addSubmitAttempted)
  const editEmailError = emailError(editForm, editSubmitAttempted)

  const handleEnroll = async (member) => {
    if (member.enrolled || enrolledMemberIds.has(member.member_id)) { return }
    setEnrolledMemberIds(withIds(member.member_id))
    // Drop the row from the cached search now; undone if the enrol fails.
    const patchResult = dispatch(
      glApi.util.updateQueryData(
        'memberSearch',
        { leagueId, roundId, q: debouncedQ },
        (draft) => {
          if (!draft.members) { return }
          const idx = draft.members.findIndex((m) => m.member_id === member.member_id)
          if (idx !== -1) { draft.members.splice(idx, 1) }
        },
      ),
    )
    // member_id is the Member the search matched, not a Player (player_round_id).
    const added = await enrollGolfer({ memberId: member.member_id, name: member.name })
    if (added) { return }
    setEnrolledMemberIds(withoutId(member.member_id))
    patchResult.undo()
  }

  // Deselecting falls back to search rather than leaving the step with nothing open.
  const handleSourceSelect = (key) => setActiveSource(key || 'search')

  // Same id as the search emits; only the bookkeeping differs.
  const handleRosterAdd = async (member) => {
    setRosterAddedIds(withIds(member.memberId))
    const added = await enrollGolfer({ memberId: member.memberId, name: member.name })
    if (added) { return }
    setRosterAddedIds(withoutId(member.memberId))
  }

  // A job for the whole list, polled until it ends; its results say per golfer what happened.
  const handleRosterAddAll = async (_fromLeagueId, memberIds) => {
    setAddAllRunning(true)
    try {
      const results = await runAddAllJob(memberIds)
      if (results === null) {
        showErrorNotification(t('group_roster.errors.add_all_failed'))
        return
      }
      // Already enrolled is not a failure, as for a single golfer.
      const failed = results.filter((entry) => !entry.success && entry.error !== 'already_enrolled').length
      // Only when all were taken: these ids come from the request, and would hide rejected golfers.
      if (failed === 0) {
        setRosterAddedIds(withIds(...memberIds))
      }
      // After the job, so the golfers it added are in the round.
      enrollRosterInSkins({ leagueId, roundId })
      dispatch(glApi.util.invalidateTags([ 'TeeSheet', 'MemberSearch' ]))
      // The count, not just "some golfers".
      if (failed > 0) {
        notifyFailure(t('group_roster.errors.some_failed', { count: failed }))
      }
    } finally {
      setAddAllRunning(false)
    }
  }

  // The job's per-golfer results, or null when it could not start or finish.
  const runAddAllJob = async (memberIds) => {
    const result = await importMembers({ leagueId, roundId, memberIds })
    if (result.error || !result.data?.job_id) { return null }
    const { outcome, data } = await pollJob(result.data.job_id)
    return outcome === REORGANIZE_OUTCOME.COMPLETED ? (data?.results || []) : null
  }

  // Only once the debounced results match the box, or a fast typist enrols the previous top hit.
  // A repeated Enter on the same text while its add runs would take the next row, which can be the same golfer
  // from another roster; a new name goes straight through.
  const handleSearchEnter = () => {
    if (addingAll || searching || debouncedQ !== searchTerm) { return }
    if (enrolling && enteredTermRef.current === searchTerm) { return }
    enteredTermRef.current = searchTerm
    const candidate = members.find((m) => !m.enrolled)
    if (candidate) { handleEnroll(candidate) }
  }

  const handleUnenroll = async (player) => {
    const result = await removeMember({ leagueId, roundId, playerRoundId: player.player_round_id })
    if (result.error) {
      showErrorNotification(t('errors.remove_failed'))
      return
    }
    // The refetched search and roster show the removed golfer again.
    setEnrolledMemberIds(new Set())
    setRosterAddedIds(new Set())
  }

  const handleEditClick = (player) => {
    const rowKey = player.player_round_id || player.id
    setConfirmId(null)
    setEditSubmitAttempted(false)
    setEditingPlayerId(rowKey)
    setEditingPlayerObj(player)
    setEditForm({
      firstName: player.first_name || '',
      lastName: player.last_name || '',
      email: player.email || '',
      handicapIndex: isPresent(player.handicap_index) ? (formatHandicap(player.handicap_index) || '') : '',
      gender: getCustomField(player.custom_fields, 'gender'),
      phone: getCustomField(player.custom_fields, phoneFieldName),
      ghin: player.handicap_network_id || '',
    })
  }

  const handleCancelEdit = () => {
    setEditingPlayerId(null)
    setEditingPlayerObj(null)
    setEditForm(EMPTY_MEMBER_FORM)
    setEditSubmitAttempted(false)
  }

  const handleSaveMember = async () => {
    setEditSubmitAttempted(true)
    if (editForm.email.trim() && !isValidEmail(editForm.email.trim())) { return }
    if (!editingPlayerObj?.player_roster_id) { return }

    const memberId = editingPlayerObj.player_roster_id
    const memberData = {
      first_name: editForm.firstName,
      last_name: editForm.lastName,
    }
    if (editForm.email) { memberData.email = editForm.email }
    // Sent as typed so Member#usga_index= parses it; editIndex is only for the optimistic row.
    const editIndex = parseHandicap(editForm.handicapIndex)
    const typedIndex = editForm.handicapIndex.trim()
    if (typedIndex) { memberData.usga_index = typedIndex }
    if (editForm.ghin) { memberData.handicap_network_id = editForm.ghin }

    // Logical keys; the server maps them and echoes the answers under the club's field names.
    const customFields = {}
    const editPhone = editForm.phone.trim()
    if (editForm.gender) { customFields.gender = editForm.gender }
    if (editPhone) { customFields.phone = editPhone }
    if (Object.keys(customFields).length) { memberData.custom_fields = customFields }

    const result = await updateMember({ leagueId, memberId, member: memberData })
    if (result.error) {
      showErrorNotification(t('errors.update_failed'))
      return
    }
    const savedCustomFields = result.data?.custom_fields || {}
    // Shown now, not from a stale refetch; only the sent fields, as blank means keep.
    setMemberEdits((prev) => ({
      ...prev,
      [memberId]: {
        ...prev[memberId],
        first_name: editForm.firstName,
        last_name: editForm.lastName,
        name: `${editForm.firstName} ${editForm.lastName}`.trim(),
        ...(editIndex !== null ? { handicap_index: editIndex } : {}),
        ...(editForm.email ? { email: editForm.email } : {}),
        ...(editForm.ghin ? { handicap_network_id: editForm.ghin } : {}),
        custom_fields: { ...(editingPlayerObj.custom_fields || {}), ...savedCustomFields },
      },
    }))
    handleCancelEdit()
  }

  const formChanger = (set) => (field, value) => set((prev) => ({ ...prev, [field]: value }))
  const handleEditFormChange = formChanger(setEditForm)
  const handleAddFormChange = formChanger(setAddForm)

  const handleCancelAddForm = () => {
    setShowAddForm(false)
    setAddForm(EMPTY_MEMBER_FORM)
    setAddSubmitAttempted(false)
  }

  const handleCreateMember = async () => {
    setAddSubmitAttempted(true)
    if (addForm.email.trim() && !isValidEmail(addForm.email.trim())) { return }

    // Logical keys, as the edit form sends them; the server maps them to the club's fields.
    const customFields = {}
    const addPhone = addForm.phone.trim()
    if (addForm.gender) { customFields.gender = addForm.gender }
    if (addPhone) { customFields.phone = addPhone }

    const result = await createMember({
      leagueId,
      roundId,
      firstName: addForm.firstName.trim(),
      lastName: addForm.lastName.trim(),
      email: addForm.email.trim() || undefined,
      handicapIndex: addForm.handicapIndex.trim() || undefined,
      ghin: addForm.ghin.trim() || undefined,
      customFields: Object.keys(customFields).length ? customFields : undefined,
    })
    if (result.error) {
      showErrorNotification(result.error?.data?.error || t('errors.add_manual_failed'))
      return
    }
    // The card, the GHIN and the handicap pull are done server-side; skins is still tms_api's.
    const memberId = result.data?.member_id
    if (memberId) { enrollInSkins({ leagueId, roundId, memberId }) }
    setShowAddForm(false)
    setAddForm(EMPTY_MEMBER_FORM)
  }

  const handleContinue = () => {
    dispatch(markStepCompleted('players'))
    dispatch(goToNextStep())
  }

  if (sheetLoading && allEnrolled.length === 0) { return <LoadingState label={t('loading')}/> }

  return (
    <div className="gl-event-setup-wrap">
      <LiveMessage message={liveMessage}/>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
      />

      <SourceTiles active={activeSource} onSelect={handleSourceSelect} emptySources={emptySources}/>

      {(activeSource === 'roster' || activeSource === 'previous_event') && (
        <SourcePicker
          // Remount per source so the paging state starts clean (REACT-089).
          key={activeSource}
          source={activeSource}
          groupId={groupId}
          groupRosterLeagueId={groupRosterLeagueId}
          currentLeagueId={leagueId}
          // displayedEnrolled, so a saved edit reaches the de-duplication.
          enrolled={displayedEnrolled}
          justAddedIds={rosterAddedIds}
          onAdd={handleRosterAdd}
          onAddAll={handleRosterAddAll}
          // No single add while Add all runs: both could import the same golfer at once.
          adding={enrolling || addingAll}
          addingAll={addingAll}
        />
      )}

      {activeSource === 'search' && (
      <div className="gl-event-setup-search-panel">
        <div className="gl-event-setup-search-panel-hd">
          <span className="gl-event-setup-search-panel-title">{t('search.panel_title')}</span>
        </div>
        <div className="gl-event-setup-search-row">
          <GolferSearchInput
            value={searchTerm}
            onChange={handleSearch}
            onEnter={handleSearchEnter}
            placeholder={t('picker.search_placeholder')}
            ariaLabel={t('search.aria_label')}
          />
        </div>
        <div className="gl-event-setup-search-results">
          {debouncedQ.length < MIN_SEARCH_LENGTH && (
            <div className="gl-event-setup-search-empty">{t('search.too_short')}</div>
          )}
          {debouncedQ.length >= MIN_SEARCH_LENGTH && searching && (
            <div className="gl-event-setup-search-empty">{t('search.searching')}</div>
          )}
          {debouncedQ.length >= MIN_SEARCH_LENGTH && !searching && members.length === 0 && (
            <div className="gl-event-setup-search-empty">{t('search.no_results', { query: debouncedQ })}</div>
          )}
          {debouncedQ.length >= MIN_SEARCH_LENGTH && !searching && members.map((m) => (
            <GolferResultRow
              key={m.member_id || m.name}
              name={m.name}
              source={m.source}
              handicapDisplay={formatHandicap(m.usga_index)}
            >
              {m.enrolled ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleUnenroll(m)}
                  disabled={unenrolling}
                  ariaLabel={t('search.remove_aria_label', { name: m.name })}
                >
                  {t('search.remove_label')}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleEnroll(m)}
                  disabled={enrolling || addingAll}
                >
                  {t('search.add')}
                </Button>
              )}
            </GolferResultRow>
          ))}
        </div>
      </div>
      )}

      {showAddForm ? (
        <div className="gl-event-setup-add-golfer-panel">
          <div className="gl-event-setup-add-golfer-title">{t('add_form.title')}</div>
          <MemberFormFields
            idPrefix="ag"
            form={addForm}
            required={{ firstName: true, lastName: true }}
            onChange={handleAddFormChange}
            emailError={addEmailError}
            radioName="ag-gender"
            phoneFieldName={phoneFieldName}
            showGhin
            autoFocusFirst
            onCancel={handleCancelAddForm}
            onSave={handleCreateMember}
            saveDisabled={creating || addingAll || !addForm.firstName.trim() || !addForm.lastName.trim()}
            saveLabel={creating ? t('add_form.adding') : t('add_form.add')}
          />
        </div>
      ) : (
        <div className="gl-event-setup-add-golfer-toggle">
          <Button variant="secondary" size="sm" onClick={() => setShowAddForm(true)}>
            {t('add_form.add_manually')}
          </Button>
        </div>
      )}

      <SectionHeader>{t('roster.header', { count: allEnrolled.length })}</SectionHeader>

      <div className="gl-event-setup-enrolled-list">
        {allEnrolled.length === 0 ? (
          <div className="gl-event-setup-empty-state">{t('roster.empty')}</div>
        ) : (
          displayedEnrolled.map((player, i) => {
            const name = player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim()
            const isPlaced = !!player.foursome_id || placed.some((p) => p.player_round_id === player.player_round_id)
            // Walk-on has no tee sheet, so there is nothing to warn about.
            const isNotOnTeeSheet = !isWalkon && placed.length > 0 && !isPlaced
            const rowKey = player.player_round_id || player.id || i
            const isConfirming = confirmId === rowKey
            const isEditing = editingPlayerId === rowKey
            const canEdit = !!player.player_roster_id

            return (
              <React.Fragment key={rowKey}>
                <div className="gl-event-setup-enrolled-item">
                  <div className="gl-event-setup-avatar" aria-hidden="true">{playerInitials(player)}</div>
                  <div className="gl-event-setup-enrolled-name">{name}</div>

                  {isEditing ? null : isConfirming ? (
                    <>
                      <span className="gl-event-setup-confirm-prompt">{t('roster.remove_prompt')}</span>
                      <button
                        className="gl-event-setup-confirm-remove-btn"
                        type="button"
                        onClick={() => { setConfirmId(null); handleUnenroll(player) }}
                        disabled={unenrolling}
                      >
                        {t('roster.confirm_remove')}
                      </button>
                      <button className="gl-event-setup-cancel-btn" type="button" onClick={() => setConfirmId(null)}>
                        {sharedT('cancel')}
                      </button>
                    </>
                  ) : (
                    <>
                      {formatHandicap(player.handicap_index) !== null && (
                        <div className="gl-event-setup-enrolled-meta">
                          {sharedT('hi_display', { value: formatHandicap(player.handicap_index) })}
                        </div>
                      )}
                      {isNotOnTeeSheet && (
                        <span className="gl-event-setup-not-on-tee-sheet-tag">{t('roster.not_on_tee_sheet')}</span>
                      )}
                      {canEdit && (
                        <button
                          className="gl-event-setup-edit-icon-btn"
                          type="button"
                          onClick={() => handleEditClick(player)}
                          aria-label={t('roster.edit_aria_label', { name })}
                        >
                          ✎
                        </button>
                      )}
                      <button
                        className="gl-event-setup-remove-icon-btn"
                        type="button"
                        onClick={() => setConfirmId(rowKey)}
                        aria-label={t('roster.remove_aria_label', { name })}
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>

                {isEditing && (
                  <div className="gl-event-setup-edit-panel">
                    <MemberFormFields
                      idPrefix={`ef-${rowKey}`}
                      form={editForm}
                      required={{ lastName: true }}
                      onChange={handleEditFormChange}
                      emailError={editEmailError}
                      radioName={`ef-gender-${rowKey}`}
                      phoneFieldName={phoneFieldName}
                      showGhin
                      onCancel={handleCancelEdit}
                      onSave={handleSaveMember}
                      saveDisabled={saving || !!editEmailError || !editForm.lastName.trim()}
                      saveLabel={saving ? t('edit_form.saving') : t('edit_form.save')}
                    />
                  </div>
                )}
              </React.Fragment>
            )
          })
        )}
      </div>

      <WizardFooter
        onBack={() => dispatch(goToPreviousStep())}
        onPrimary={handleContinue}
        primaryLabel={t('continue')}
      />
    </div>
  )
}

export default PlayersStep
